import { isPlatformBrowser } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import type { AuthenticationResult } from '@azure/msal-browser';
import { take } from 'rxjs/operators';

import { AuthService } from './auth.service';
import { getAzureAdConfig, isAzureAdEnabled } from './azure-ad-env';
import { MsalTokenService } from './msal-token.service';

/**
 * No meaningful activity for this long → logout (tab still open).
 * 30 minutes was easy to hit while reading the UI (no clicks) or relying on API-only usage;
 * align with typical work sessions; token refresh still limits API access server-side.
 */
const SESSION_IDLE_MS = 8 * 60 * 60 * 1000;

/** Refresh this long before access JWT `exp` while the user is active (silent renewal). */
const ACCESS_TOKEN_REFRESH_LEAD_MS = 5 * 60 * 1000;

/** Do not schedule proactive refresh sooner than this (avoids tight loops). */
const MIN_SCHEDULE_DELAY_MS = 10_000;

/** Throttle activity timestamps (mousemove etc. would fire too often). */
const ACTIVITY_THROTTLE_MS = 1000;

/** If JWT has no `exp`, refresh on this interval while active (fallback). */
const FALLBACK_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

/**
 * While authenticated: tracks user activity, logs out after {@link SESSION_IDLE_MS} of idleness,
 * and refreshes the access token before expiry when the user is active (so APIs keep working
 * without waiting for 401). Complements {@link authInterceptor} retry-on-401.
 *
 * **Microsoft (MSAL) only:** when there is no SPICE refresh token, proactively calls
 * `acquireTokenSilent` before the cached token’s `expiresOn` so {@link azureAdApiInterceptor}
 * keeps getting valid tokens without silent-auth failures at expiry.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  /** Present only when Entra is enabled in `app.config`. */
  private readonly msal = inject(MsalService, { optional: true });
  private readonly msalToken = inject(MsalTokenService);

  private lastActivityAt = Date.now();
  private lastActivityThrottleAt = 0;

  private idleIntervalId: ReturnType<typeof setInterval> | null = null;
  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  /** Proactive MSAL silent renewal timer (separate from SPICE JWT {@link refreshTimeoutId}). */
  private msalRefreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly boundListeners: Array<{ type: string; fn: EventListener }> = [];

  constructor() {
    effect(() => {
      if (this.auth.isAuthenticated()) {
        this.startSessionWatchers();
      } else {
        this.stopSessionWatchers();
      }
    });
  }

  private startSessionWatchers(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.stopSessionWatchers();

    this.lastActivityAt = Date.now();
    this.lastActivityThrottleAt = 0;

    const onActivity = (): void => this.recordActivityThrottled();

    const eventTypes = [
      'pointerdown',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
      /** Hover / reading still counts as “using the app”; throttled below. */
      'mousemove'
    ] as const;
    for (const type of eventTypes) {
      document.addEventListener(type, onActivity, { passive: true, capture: true });
      this.boundListeners.push({ type, fn: onActivity as EventListener });
    }

    const onVisible = (): void => {
      if (!document.hidden) {
        this.recordActivityThrottled();
      }
    };
    document.addEventListener('visibilitychange', onVisible, { passive: true });
    this.boundListeners.push({ type: 'visibilitychange', fn: onVisible as EventListener });

    this.idleIntervalId = window.setInterval(() => this.checkIdleTimeout(), 30_000);

    if (this.auth.getRefreshToken()) {
      this.scheduleProactiveTokenRefresh();
    } else if (isAzureAdEnabled() && this.msal && this.msalToken.tryEnsureMsalActiveAccount()) {
      /** Entra-only session: renew MSAL cache before access/id token expiry (same scopes as API interceptor). */
      this.scheduleMsalProactiveTokenRefresh();
    }
  }

  private stopSessionWatchers(): void {
    if (this.idleIntervalId != null) {
      clearInterval(this.idleIntervalId);
      this.idleIntervalId = null;
    }
    if (this.refreshTimeoutId != null) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
    if (this.msalRefreshTimeoutId != null) {
      clearTimeout(this.msalRefreshTimeoutId);
      this.msalRefreshTimeoutId = null;
    }
    for (const { type, fn } of this.boundListeners) {
      document.removeEventListener(type, fn, true);
    }
    this.boundListeners.length = 0;
  }

  private recordActivityThrottled(): void {
    const now = Date.now();
    if (now - this.lastActivityThrottleAt < ACTIVITY_THROTTLE_MS) {
      return;
    }
    this.lastActivityThrottleAt = now;
    this.lastActivityAt = now;
  }

  /** Successful API traffic counts as activity (idle timeout + proactive refresh gate). */
  signalUserActivity(): void {
    if (!isPlatformBrowser(this.platformId) || !this.auth.isAuthenticated()) {
      return;
    }
    this.recordActivityThrottled();
  }

  private checkIdleTimeout(): void {
    if (!this.auth.isAuthenticated()) {
      return;
    }
    if (Date.now() - this.lastActivityAt <= SESSION_IDLE_MS) {
      return;
    }
    this.ngZone.run(() => {
      const msalRedirect = this.auth.logout('SESSION_IDLE_TIMEOUT');
      if (!msalRedirect) {
        void this.router.navigateByUrl('/login');
      }
    });
  }

  private scheduleProactiveTokenRefresh(): void {
    if (this.refreshTimeoutId != null) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
    if (!this.auth.isAuthenticated() || !this.auth.getRefreshToken()) {
      return;
    }

    const expMs = this.auth.getAccessTokenExpiryMs();
    if (expMs == null) {
      this.refreshTimeoutId = window.setTimeout(
        () => this.runProactiveTokenRefresh(),
        FALLBACK_REFRESH_INTERVAL_MS
      );
      return;
    }

    const now = Date.now();
    const msUntilExpiry = expMs - now;

    let delayMs: number;
    if (msUntilExpiry <= 0) {
      delayMs = 2000;
    } else if (msUntilExpiry <= ACCESS_TOKEN_REFRESH_LEAD_MS) {
      delayMs = Math.max(2000, msUntilExpiry - 60_000);
    } else {
      delayMs = Math.max(MIN_SCHEDULE_DELAY_MS, msUntilExpiry - ACCESS_TOKEN_REFRESH_LEAD_MS);
    }

    this.refreshTimeoutId = window.setTimeout(() => this.runProactiveTokenRefresh(), delayMs);
  }

  private runProactiveTokenRefresh(): void {
    this.refreshTimeoutId = null;

    if (!this.auth.isAuthenticated() || !this.auth.getRefreshToken()) {
      return;
    }

    if (Date.now() - this.lastActivityAt > SESSION_IDLE_MS) {
      return;
    }

    this.auth
      .refreshToken()
      .pipe(take(1))
      .subscribe({
        next: (token) => {
          if (token && this.auth.isAuthenticated()) {
            this.scheduleProactiveTokenRefresh();
          }
        }
      });
  }

  /**
   * Schedules {@link runMsalProactiveTokenRefresh} before MSAL `expiresOn` (same lead as SPICE JWT).
   * When `fromResult` is set (after a successful silent acquire), skips an extra network round-trip.
   */
  private scheduleMsalProactiveTokenRefresh(fromResult?: AuthenticationResult): void {
    if (this.msalRefreshTimeoutId != null) {
      clearTimeout(this.msalRefreshTimeoutId);
      this.msalRefreshTimeoutId = null;
    }
    if (!isPlatformBrowser(this.platformId) || !this.auth.isAuthenticated()) {
      return;
    }
    if (!isAzureAdEnabled() || this.auth.getRefreshToken()) {
      return;
    }
    const msal = this.msal;
    if (!msal || getAzureAdConfig() == null || !this.msalToken.tryEnsureMsalActiveAccount()) {
      return;
    }

    const armTimerFromResult = (result: AuthenticationResult): void => {
      const expMs = result.expiresOn?.getTime() ?? null;
      let delayMs: number;
      if (expMs == null) {
        delayMs = FALLBACK_REFRESH_INTERVAL_MS;
      } else {
        const now = Date.now();
        const msUntilExpiry = expMs - now;
        if (msUntilExpiry <= 0) {
          delayMs = 2000;
        } else if (msUntilExpiry <= ACCESS_TOKEN_REFRESH_LEAD_MS) {
          delayMs = Math.max(2000, msUntilExpiry - 60_000);
        } else {
          delayMs = Math.max(MIN_SCHEDULE_DELAY_MS, msUntilExpiry - ACCESS_TOKEN_REFRESH_LEAD_MS);
        }
      }
      this.msalRefreshTimeoutId = window.setTimeout(() => this.runMsalProactiveTokenRefresh(), delayMs);
    };

    if (fromResult) {
      armTimerFromResult(fromResult);
      return;
    }

    this.msalToken
      .acquireTokenSilentForApi()
      .pipe(take(1))
      .subscribe({
        next: (result) => armTimerFromResult(result),
        error: () => {
          this.msalRefreshTimeoutId = window.setTimeout(
            () => this.scheduleMsalProactiveTokenRefresh(),
            5 * 60 * 1000
          );
        }
      });
  }

  /** Runs while the user is under the idle cap; refreshes MSAL tokens so API calls keep succeeding. */
  private runMsalProactiveTokenRefresh(): void {
    this.msalRefreshTimeoutId = null;
    if (!isPlatformBrowser(this.platformId) || !this.auth.isAuthenticated()) {
      return;
    }
    if (!isAzureAdEnabled() || this.auth.getRefreshToken()) {
      return;
    }
    if (Date.now() - this.lastActivityAt > SESSION_IDLE_MS) {
      return;
    }
    const msal = this.msal;
    if (!msal || getAzureAdConfig() == null || !this.msalToken.tryEnsureMsalActiveAccount()) {
      return;
    }

    this.msalToken
      .acquireTokenSilentForApi()
      .pipe(take(1))
      .subscribe({
        next: (result) => this.scheduleMsalProactiveTokenRefresh(result),
        error: () => {
          this.msalRefreshTimeoutId = window.setTimeout(
            () => this.scheduleMsalProactiveTokenRefresh(),
            5 * 60 * 1000
          );
        }
      });
  }
}
